<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import { EntityDiscussionItem, EntityDiscussionSkeleton } from '~/components/entity/discussion'
import type { IMeiliDiscussionHit, IDiscussionListProps as IProps } from '~/components/entity/discussion'

const config = useRuntimeConfig()
const router = useRouter()
const { locale } = useI18n()

interface IComponentProps extends IProps {
  categoryId?: string | null
}

const props = defineProps<IComponentProps>()
const route = useRoute()
const hitsPerPage = Number(props.queryParams?.per_page || 10)

const initialQuery = String(route.query.q || '')
const searchQueryString = ref(initialQuery)
const debouncedQuery = useDebounce(searchQueryString, 500)
const currentPage = ref(Number(route.query.page) || 1)
const categoryId = ref<string | null>(props.categoryId || null)

// Compute search key based on all filter dependencies
const searchKey = computed(() =>
  ['discussions-search', debouncedQuery.value, currentPage.value, categoryId.value || 'all'].join(
    '-'
  )
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IMeiliDiscussionHit> | null>(searchKey, async () => {
  const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_questions`)

  try {
    const filterQuery = categoryId.value ? `categories.id = "${categoryId.value}"` : undefined

    return (await search(debouncedQuery.value, {
      hitsPerPage,
      page: currentPage.value,
      attributesToHighlight: ['title'],
      filter: filterQuery,
      sort: ['updated_at:desc']
    })) as unknown as SearchResponse<IMeiliDiscussionHit>
  } catch (e) {
    console.error('MeiliSearch fetch error:', e)
    return null
  }
})

const hasError = computed(() => !!searchError.value)

const typedHits = computed<IMeiliDiscussionHit[]>(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title || hit.title,
    url: hit.url || '',
    description: hit.description,
    categories: hit.categories?.map((cat) => ({
      ...cat,
      title: typeof cat.title === 'object' ? (cat.title[locale.value as 'ru' | 'ky'] ?? cat.title.ru ?? '') : cat.title
    })),
    updated_at: hit.updated_at
  }))
})

watch(debouncedQuery, () => {
  currentPage.value = 1
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: debouncedQuery.value || undefined,
      page: undefined
    }
  })
})

const changePage = (page: number) => {
  currentPage.value = page
  router.replace({
    path: route.path,
    query: { ...route.query, page: page > 1 ? String(page) : undefined }
  })
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />

      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="discussions">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="discussions" tag="h2" class="sr-only">{{
          $t('page.discussions.category_page.breadcrumbs')
        }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityDiscussionItem :item="item" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{
          $t('widget.discussions.list_meili_search.no_discussions')
        }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityDiscussionSkeleton :count="Number(props.queryParams?.per_page || 3)" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('widget.discussions.list_meili_search.error_title') }}</AlertTitle>
        <AlertDescription>
          {{ $t('widget.discussions.list_meili_search.error_description') }}
        </AlertDescription>
      </Alert>
    </section>
  </div>
</template>
